import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CalendlyEmbed from "./CalendlyEmbed";
import { track } from "../lib/tracking";
import { getUtms } from "../lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GESAMT = 6;
const FRAGE_KEYS = ["f1", "f2", "f3", "f4", "f5"];

const OPTIONEN = {
  f2: ["unter 500", "500 bis 5.000", "5.000 bis 20.000", "mehr als 20.000"],
  f3: ["unter 20.000 €", "20.000 bis 50.000 €", "50.000 bis 250.000 €", "mehr als 250.000 €"],
  f4: ["unter 3.000 €", "3.000 bis 5.000 €", "mehr als 5.000 €"],
  f5: ["Geschäftsführer oder Inhaber", "Partner oder Vorstand", "Vertriebsleitung", "Marketing", "andere"]
};

const FRAGE_TITEL = {
  f1: "An wen verkaufen Sie? Bitte nennen Sie Jobtitel und Branche Ihrer Entscheider.",
  f2: "Wie viele dieser Entscheider gibt es im DACH-Raum ungefähr?",
  f3: "Was ist ein neuer Kunde für Sie wert (Auftrags- oder Jahreswert)?",
  f4: "Welches monatliche Budget ist für Ihre Neukundengewinnung realistisch?",
  f5: "Welche Rolle haben Sie im Unternehmen?"
};

const inputKlasse =
  "mt-2 w-full rounded-[2px] border border-navy/20 bg-white px-4 py-3 text-base text-textdunkel placeholder:text-gray-400 outline-none transition-colors focus:border-navy focus:ring-1 focus:ring-navy/25";
const labelKlasse = "block text-xs font-semibold uppercase tracking-[0.12em] text-textgrau";

export default function AnfrageForm() {
  const navigate = useNavigate();
  const [schritt, setSchritt] = useState(0);
  const [kontakt, setKontakt] = useState({ vorname: "", nachname: "", firma: "", email: "", telefon: "" });
  const [antworten, setAntworten] = useState({ f1: "", f2: "", f3: "", f4: "", f5: "" });
  const [anfrageId, setAnfrageId] = useState(() => sessionStorage.getItem("2bs_anfrage_id") || "");
  const [fehler, setFehler] = useState("");
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    if (schritt !== 6) return;
    function onMessage(e) {
      if (e.origin !== "https://calendly.com") return;
      if (!e.data || e.data.event !== "calendly.event_scheduled") return;
      const flagKey = "2bs_termin_gebucht";
      if (!sessionStorage.getItem(flagKey)) {
        sessionStorage.setItem(flagKey, "1");
        const ereignisDaten = {
          email: kontakt.email,
          phone: kontakt.telefon,
          vorname: kontakt.vorname,
          nachname: kontakt.nachname,
          customData: { segment: getUtms().utm_content || "" }
        };
        track("Termin_gebucht", ereignisDaten);
      }
      fetch(`${API}/anfrage/${anfrageId}/termin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ calendly_payload: e.data.payload || {} })
      })
        .then((antwort) => {
          if (antwort.ok) {
            sessionStorage.removeItem("2bs_termin_gebucht");
            sessionStorage.removeItem("2bs_anfrage_id");
          }
        })
        .catch(() => {});
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [schritt, anfrageId, kontakt]);

  function kontaktValid() {
    return (
      kontakt.vorname.trim() &&
      kontakt.nachname.trim() &&
      kontakt.firma.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(kontakt.email.trim())
    );
  }

  function frageWeiter(schluessel) {
    const wert = antworten[schluessel];
    if (schluessel === "f1" ? !wert.trim() : !wert) return;
    setFehler("");
    setSchritt((s) => s + 1);
  }

  async function absenden(e) {
    e.preventDefault();
    setFehler("");
    if (!kontaktValid()) {
      setFehler("Bitte füllen Sie Vorname, Nachname, Firma und geschäftliche E-Mail aus.");
      return;
    }
    setSendet(true);
    try {
      const utm = getUtms();
      let kid = anfrageId;
      if (kid) {
        const r0 = await fetch(`${API}/anfrage/${kid}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kontakt })
        });
        if (!r0.ok) kid = "";
      }
      if (!kid) {
        const r = await fetch(`${API}/anfrage/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...kontakt, utm, fbclid: utm.fbclid || "", page_url: window.location.href })
        });
        if (!r.ok) throw new Error("start fehlgeschlagen");
        const d = await r.json();
        kid = d.id;
        sessionStorage.setItem("2bs_anfrage_id", kid);
        setAnfrageId(kid);
      }
      const r2 = await fetch(`${API}/anfrage/${kid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ antworten, abschluss: true })
      });
      if (!r2.ok) throw new Error("abschluss fehlgeschlagen");
      const d2 = await r2.json();
      const ereignisDaten = {
        email: kontakt.email,
        phone: kontakt.telefon,
        vorname: kontakt.vorname,
        nachname: kontakt.nachname,
        customData: { ergebnis: d2.ergebnis, segment: utm.utm_content || "" }
      };
      track("Lead_Formular", ereignisDaten);
      if (d2.ergebnis === "absage") {
        navigate("/kein-passender-termin");
      } else {
        track("Lead_Qualifiziert", ereignisDaten);
        setSchritt(6);
      }
    } catch (err) {
      setFehler("Das Absenden ist leider fehlgeschlagen. Bitte versuchen Sie es erneut.");
    }
    setSendet(false);
  }

  const utm = getUtms();
  const kalenderParams = {
    name: `${kontakt.vorname} ${kontakt.nachname}`.trim(),
    email: kontakt.email,
    a1: antworten.f1,
    a2: antworten.f2,
    a3: antworten.f3,
    a4: antworten.f4,
    a5: antworten.f5,
    a6: kontakt.firma,
    utm_source: utm.utm_source || "",
    utm_medium: utm.utm_medium || "",
    utm_campaign: utm.utm_campaign || "",
    utm_content: utm.utm_content || "",
    utm_term: utm.utm_term || ""
  };

  return (
    <div className="rounded-[2px] bg-white p-6 shadow-[0_32px_80px_-40px_rgba(15,35,60,0.5)] sm:p-10" data-testid="anfrage-formular">
      {schritt <= 5 && (
        <>
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.15em] text-textgrau">
            <span data-testid="formular-schritt">Schritt {schritt + 1} von {GESAMT}</span>
            <span>{Math.round(((schritt + 1) / GESAMT) * 100)} %</span>
          </div>
          <div className="mt-3 h-[3px] w-full bg-hellblau">
            <div
              className="h-full bg-gruen transition-all duration-300"
              style={{ width: `${((schritt + 1) / GESAMT) * 100}%` }}
              data-testid="formular-fortschritt"
            />
          </div>

          {schritt <= 4 && (
            <div className="mt-8" data-testid={`formular-schritt-frage-${schritt + 1}`}>
              <p className="text-base font-semibold leading-relaxed text-textdunkel">
                {FRAGE_TITEL[FRAGE_KEYS[schritt]]}
              </p>

              {FRAGE_KEYS[schritt] === "f1" ? (
                <textarea
                  rows={4}
                  data-testid="frage-f1-antwort"
                  className={inputKlasse}
                  value={antworten.f1}
                  onChange={(e) => setAntworten({ ...antworten, f1: e.target.value })}
                />
              ) : (
                <div className="mt-5 space-y-3">
                  {OPTIONEN[FRAGE_KEYS[schritt]].map((opt, idx) => {
                    const aktiv = antworten[FRAGE_KEYS[schritt]] === opt;
                    return (
                      <button
                        type="button"
                        key={opt}
                        data-testid={`frage-${FRAGE_KEYS[schritt]}-option-${idx + 1}`}
                        onClick={() => setAntworten({ ...antworten, [FRAGE_KEYS[schritt]]: opt })}
                        className={`flex w-full items-center gap-3 rounded-[2px] border px-5 py-4 text-left text-base transition-colors ${
                          aktiv ? "border-navy bg-hellblau/60 text-textdunkel" : "border-navy/15 text-textdunkel hover:border-navy/50"
                        }`}
                      >
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${aktiv ? "border-navy" : "border-navy/30"}`}>
                          {aktiv && <span className="h-2.5 w-2.5 rounded-full bg-navy" />}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}

              {fehler && <p className="mt-4 text-sm text-red-600" data-testid="formular-fehler">{fehler}</p>}

              <div className="mt-6 flex items-center justify-between gap-4">
                <button
                  type="button"
                  data-testid="formular-zurueck"
                  onClick={() => setSchritt(Math.max(0, schritt - 1))}
                  className="text-xs font-semibold uppercase tracking-[0.12em] text-textgrau transition-colors hover:text-textdunkel"
                >
                  Zurück
                </button>
                <button
                  type="button"
                  disabled={FRAGE_KEYS[schritt] === "f1" ? !antworten.f1.trim() : !antworten[FRAGE_KEYS[schritt]]}
                  data-testid={`formular-weiter-frage-${schritt + 1}`}
                  onClick={() => frageWeiter(FRAGE_KEYS[schritt])}
                  className="rounded-[2px] bg-gruen px-8 py-4 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-gruenhover disabled:opacity-60"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}

          {schritt === 5 && (
            <form onSubmit={absenden} className="mt-8" data-testid="formular-schritt-kontakt">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="vorname" className={labelKlasse}>Vorname</label>
                  <input id="vorname" data-testid="anfrage-vorname" className={inputKlasse} value={kontakt.vorname}
                    onChange={(e) => setKontakt({ ...kontakt, vorname: e.target.value })} required />
                </div>
                <div>
                  <label htmlFor="nachname" className={labelKlasse}>Nachname</label>
                  <input id="nachname" data-testid="anfrage-nachname" className={inputKlasse} value={kontakt.nachname}
                    onChange={(e) => setKontakt({ ...kontakt, nachname: e.target.value })} required />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="firma" className={labelKlasse}>Firma</label>
                <input id="firma" data-testid="anfrage-firma" className={inputKlasse} value={kontakt.firma}
                  onChange={(e) => setKontakt({ ...kontakt, firma: e.target.value })} required />
              </div>
              <div className="mt-4">
                <label htmlFor="email" className={labelKlasse}>Geschäftliche E-Mail</label>
                <input id="email" type="email" data-testid="anfrage-email" className={inputKlasse} value={kontakt.email}
                  onChange={(e) => setKontakt({ ...kontakt, email: e.target.value })} required />
              </div>
              <div className="mt-4">
                <label htmlFor="telefon" className={labelKlasse}>Telefon (optional)</label>
                <input id="telefon" type="tel" data-testid="anfrage-telefon" className={inputKlasse} value={kontakt.telefon}
                  onChange={(e) => setKontakt({ ...kontakt, telefon: e.target.value })} />
              </div>
              {fehler && <p className="mt-4 text-sm text-red-600" data-testid="formular-fehler">{fehler}</p>}
              <div className="mt-6 flex items-center justify-between gap-4">
                <button
                  type="button"
                  data-testid="formular-zurueck"
                  onClick={() => setSchritt(4)}
                  className="text-xs font-semibold uppercase tracking-[0.12em] text-textgrau transition-colors hover:text-textdunkel"
                >
                  Zurück
                </button>
                <button type="submit" disabled={sendet} data-testid="formular-absenden"
                  className="rounded-[2px] bg-gruen px-8 py-4 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-gruenhover disabled:opacity-60">
                  {sendet ? "Wird gesendet ..." : "Termin auswählen"}
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {schritt === 6 && (
        <div data-testid="kalender-schritt">
          <p className="text-lg font-semibold text-textdunkel">Wählen Sie direkt einen Termin.</p>
          <p className="mt-1 text-sm leading-relaxed text-textgrau">
            Ihre Angaben sind gespeichert. Im Kalender wählen Sie einen freien Zeitpunkt für Ihre 45 Minuten mit Patrick Häfner.
          </p>
          <div className="mt-6">
            <CalendlyEmbed params={kalenderParams} />
          </div>
        </div>
      )}
    </div>
  );
}
